import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, User, Phone, Mail, MapPin, FileText, 
  CheckCircle, AlertCircle, Loader2, ArrowLeft, Briefcase
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

const EnterpriseContactPage = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    company_name: '',
    siret: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_address: '',
    company_postal_code: '',
    company_city: '',
    preferred_contact_method: 'email',
    request_description: '',
    additional_info: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/enterprise-requests`, formData);
      
      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Une erreur est survenue');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi du formulaire');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Demande envoyée !</h2>
          <p className="text-slate-600 mb-6">
            Nous avons bien reçu votre demande de contact professionnel. 
            Notre équipe commerciale vous recontactera dans les plus brefs délais.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Référence : #{formData.company_name.substring(0, 10).toUpperCase()}
          </p>
          <Button onClick={() => navigate('/')} className="w-full bg-slate-800 hover:bg-slate-700">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="DLV Express" className="h-12 cursor-pointer" onClick={() => navigate('/')} />
          <Button variant="outline" onClick={() => navigate('/')} className="text-white border-white/30 hover:bg-white/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au site
          </Button>
        </div>
      </header>
      
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4">
            <Building2 className="w-4 h-4" />
            Entreprises & Professionnels
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Demande de contact professionnel</h1>
          <p className="text-lg text-white/70">
            Vous souhaitez établir un partenariat ou obtenir des informations sur nos services B2B ?
            <br />Remplissez ce formulaire et notre équipe vous recontactera.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">
          {/* Company Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Informations de l'entreprise
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de l'entreprise *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Ex: SARL Dupont Logistics"
                  required
                />
              </div>
              <div>
                <Label>Numéro SIRET *</Label>
                <Input
                  value={formData.siret}
                  onChange={(e) => handleChange('siret', e.target.value)}
                  placeholder="Ex: 123 456 789 00012"
                  maxLength={17}
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Company Address */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-600" />
              Adresse du siège social
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Adresse *</Label>
                <Input
                  value={formData.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                  placeholder="Ex: 15 rue de l'Industrie"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Code postal *</Label>
                  <Input
                    value={formData.company_postal_code}
                    onChange={(e) => handleChange('company_postal_code', e.target.value)}
                    placeholder="95500"
                    maxLength={5}
                    required
                  />
                </div>
                <div>
                  <Label>Ville *</Label>
                  <Input
                    value={formData.company_city}
                    onChange={(e) => handleChange('company_city', e.target.value)}
                    placeholder="Gonesse"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Person */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              Personne à contacter
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nom du contact *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  placeholder="Ex: Jean Dupont"
                  required
                />
              </div>
              <div>
                <Label>Email professionnel *</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="contact@entreprise.fr"
                  required
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  placeholder="01 23 45 67 89"
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Preferred Contact Method */}
          <div>
            <Label>Moyen de contact préféré</Label>
            <div className="flex gap-6 mt-2">
              {[
                { value: 'email', label: 'Email', icon: Mail },
                { value: 'phone', label: 'Téléphone', icon: Phone }
              ].map(method => (
                <label key={method.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="contact_method"
                    value={method.value}
                    checked={formData.preferred_contact_method === method.value}
                    onChange={(e) => handleChange('preferred_contact_method', e.target.value)}
                    className="w-4 h-4"
                  />
                  <method.icon className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">{method.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Request Description */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Votre demande
            </h3>
            <div>
              <Label>Description de votre besoin *</Label>
              <Textarea
                value={formData.request_description}
                onChange={(e) => handleChange('request_description', e.target.value)}
                placeholder="Décrivez votre activité, vos besoins en transport/logistique, le volume estimé, la fréquence souhaitée..."
                className="min-h-[120px]"
                required
              />
            </div>
            <div className="mt-4">
              <Label>Informations complémentaires</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => handleChange('additional_info', e.target.value)}
                placeholder="Toute information utile : contraintes particulières, délais, zones géographiques..."
                className="min-h-[80px]"
              />
            </div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Submit */}
          <div className="pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="w-full bg-slate-800 hover:bg-slate-700 py-6 text-lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Briefcase className="w-5 h-5 mr-2" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 text-center">
            En soumettant ce formulaire, vous acceptez d'être recontacté par notre équipe commerciale.
          </p>
        </form>
      </div>
    </div>
  );
};

export default EnterpriseContactPage;
